import {m} from 'framer-motion';
import {useState, useEffect} from 'react';
import {alpha} from '@mui/material/styles';
import Box, {BoxProps} from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Logo from '../logo';

export default function SplashScreen({sx, ...other}: BoxProps) {
  const [mounted, setMounted] = useState(false);
  const [showReloadLink, setShowReloadLink] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.includes('bytepad/login')
    ) {
      const timer = window.setTimeout(() => setShowReloadLink(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!mounted) return null;

  const handleReload = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.reload();
  };

  return (
    <Box
      sx={{
        right: 0,
        width: 1,
        bottom: 0,
        height: 1,
        zIndex: 9998,
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        ...sx
      }}
      {...other}>
      {/* ====== STACKED ANIMATION WRAPPER ====== */}
      <Box
        sx={{
          position: 'relative',
          width: 160, // lock a square so rings don't distort layout
          height: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        {/* Rings sit BEHIND (zIndex: 0) and ignore clicks */}
        <Box
          component={m.div}
          animate={{
            scale: [1.6, 1, 1, 1.6, 1.6],
            rotate: [270, 0, 0, 270, 270],
            opacity: [0.25, 1, 1, 1, 0.25],
            borderRadius: ['25%', '25%', '50%', '50%', '25%']
          }}
          transition={{ease: 'linear', duration: 3.2, repeat: Infinity}}
          sx={{
            position: 'absolute',
            inset: 0,
            margin: 'auto',
            width: 100,
            height: 100,
            zIndex: 0,
            pointerEvents: 'none',
            border: theme =>
              `solid 3px ${alpha(theme.palette.primary.dark, 0.24)}`
          }}
        />
        <Box
          component={m.div}
          animate={{
            scale: [1, 1.2, 1.2, 1, 1],
            rotate: [0, 270, 270, 0, 0],
            opacity: [1, 0.25, 0.25, 0.25, 1],
            borderRadius: ['25%', '25%', '50%', '50%', '25%']
          }}
          transition={{ease: 'linear', duration: 3.2, repeat: Infinity}}
          sx={{
            position: 'absolute',
            inset: 0,
            margin: 'auto',
            width: 120,
            height: 120,
            zIndex: 0,
            pointerEvents: 'none',
            border: theme =>
              `solid 8px ${alpha(theme.palette.primary.dark, 0.24)}`
          }}
        />

        {/* Logo sits ABOVE rings */}
        <m.div
          style={{zIndex: 1}}
          animate={{
            scale: [1, 0.9, 0.9, 1, 1],
            opacity: [1, 0.48, 0.48, 1, 1]
          }}
          transition={{
            duration: 2,
            ease: 'easeInOut',
            repeatDelay: 1,
            repeat: Infinity
          }}>
          <Logo disabledLink />
        </m.div>
      </Box>

      {/* ====== RELOAD TEXT BELOW ANIMATION ====== */}
      {showReloadLink && (
        <Box
          component={m.div}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{duration: 0.8}}
          sx={{mt: 3, zIndex: 1}} // margin below the animation, sits above rings
        >
          <Typography variant="body2" sx={{color: 'text.secondary'}}>
            Click{' '}
            <Link
              component="button"
              onClick={handleReload}
              underline="always"
              color="primary"
              sx={{
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': {opacity: 0.8}
              }}>
              here
            </Link>{' '}
            to go back to login page.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
